import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PaymentsAnalyticsPanel from "@/components/PaymentsAnalyticsPanel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { DollarSign, TrendingUp, TrendingDown, Wallet, Receipt, Car, FileBarChart, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "this_quarter", label: "This quarter" },
  { key: "ytd", label: "Year to date" },
  { key: "last_year", label: "Previous year" },
  { key: "custom", label: "Custom range" },
];

const money = (v) => `$${(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Financials() {
  const { user } = useAuth() || {};
  const isOwner = user?.is_super_admin || user?.role === "owner";
  const [preset, setPreset] = useState("this_month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = preset === "custom" ? { start, end } : { preset };
      const { data: d } = await api.get("/financials/dashboard", { params });
      setData(d);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (preset !== "custom" || (start && end)) load(); /* eslint-disable-next-line */ }, [preset, start, end]);

  const cats = data?.expense_categories || {};
  const sortedCats = Object.entries(cats).sort((a, b) => b[1] - a[1]);

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Financials</div>
          <h1 className="font-heading text-4xl font-bold flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-[#FF5A00]"/> Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/financials/expenses"><Button variant="outline" size="sm" data-testid="fin-nav-expenses"><Receipt className="w-4 h-4 mr-1"/>Expenses</Button></Link>
          <Link to="/app/financials/mileage"><Button variant="outline" size="sm" data-testid="fin-nav-mileage"><Car className="w-4 h-4 mr-1"/>Mileage</Button></Link>
          <Link to="/app/financials/statements"><Button variant="outline" size="sm" data-testid="fin-nav-statements"><FileBarChart className="w-4 h-4 mr-1"/>Statements</Button></Link>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="fin-filter-bar">
        <Select value={preset} onValueChange={setPreset}>
          <SelectTrigger className="w-44" data-testid="fin-preset-select"><SelectValue/></SelectTrigger>
          <SelectContent>{PRESETS.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
        {preset === "custom" && (
          <>
            <Input type="date" value={start} onChange={(e)=>setStart(e.target.value)} className="w-40" data-testid="fin-start-input"/>
            <Input type="date" value={end} onChange={(e)=>setEnd(e.target.value)} className="w-40" data-testid="fin-end-input"/>
          </>
        )}
      </div>

      {loading ? (
        <div className="p-12 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin inline"/></div>
      ) : !data ? (
        <div className="p-12 text-center text-zinc-400">No data.</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card label="Gross Revenue" value={money(data.gross_revenue)} icon={TrendingUp} color="text-emerald-600" testid="fin-kpi-revenue"/>
            <Card label="Total Expenses" value={money(data.total_expenses)} icon={TrendingDown} color="text-rose-600" testid="fin-kpi-expenses"/>
            <Card label="Net Income" value={money(data.net_income)} icon={Wallet} color={data.net_income >= 0 ? "text-emerald-600" : "text-rose-600"} sub={`${data.net_to_gross_pct}% margin`} testid="fin-kpi-net"/>
            <Card label="Accounts Receivable" value={money(data.accounts_receivable)} icon={Receipt} color="text-amber-600" sub={`${data.outstanding_invoices_count} invoices`} testid="fin-kpi-ar"/>
          </div>

          {/* Category breakdown + mileage */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl card-crisp p-6">
              <div className="font-heading font-bold text-lg mb-4">Expense Breakdown</div>
              {sortedCats.length === 0 ? (
                <div className="text-sm text-zinc-400 text-center py-8">No expenses in this period.</div>
              ) : (
                <div className="space-y-3">
                  {sortedCats.map(([name, amount]) => {
                    const pctG = data.expense_category_pct_of_gross[name] || 0;
                    const pctE = data.expense_category_pct_of_expenses[name] || 0;
                    return (
                      <div key={name} data-testid={`fin-cat-${name}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="font-medium">{name}</span>
                          <span className="font-mono-accent">{money(amount)} · {pctE.toFixed(1)}%</span>
                        </div>
                        <div className="w-full bg-zinc-100 rounded-full h-2 overflow-hidden">
                          <div className="bg-[#FF5A00] h-2" style={{ width: `${Math.min(100, pctE)}%` }}/>
                        </div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">{pctG.toFixed(1)}% of gross revenue</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6">
              <div className="font-heading font-bold text-lg mb-4">Mileage</div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-zinc-500">Total miles</span><span className="font-mono-accent font-semibold">{data.mileage.total_miles}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Rate</span><span className="font-mono-accent">${data.mileage.rate}/mi</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Deductible value</span><span className="font-mono-accent font-semibold text-emerald-600">{money(data.mileage.value)}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Trips</span><span className="font-mono-accent">{data.mileage.trips_count}</span></div>
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-100 text-xs text-zinc-500">
                Adjust the mileage rate in <Link to="/app/settings" className="text-[#FF5A00] hover:underline">Settings</Link>.
              </div>
            </div>
          </div>

          <div className="mt-6 text-xs text-zinc-500 flex items-center gap-1"><Calendar className="w-3 h-3"/> Period: {data.start?.slice(0,10)} → {data.end?.slice(0,10)} · LeadForge is not a substitute for professional accounting or tax advice.</div>
        </>
      )}

      {/* Payments Analytics — Task 3 (owner-only) */}
      {isOwner && <PaymentsAnalyticsPanel />}
    </div>
  );
}

function Card({ label, value, icon: Icon, color, sub, testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
        <Icon className={`w-5 h-5 ${color}`}/>
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}
